// Detection of contour fragments that are easy to miss when inspecting a structure visually.
// Works on raw contour data (as loaded from / saved to the RTSTRUCT), not on the rendered SDF.

type ContourPoint = number[];
type RoiContourData = { [sliceId: string]: { polygons?: any[], points?: ContourPoint[] } };

export enum StrayContourKind {
    /** Fewer than 4 points: never rendered (see Sdf.addContours), but still present in the data. */
    Degenerate = 'degenerate',
    /** Slice id that doesn't match any image slice: never rendered, but still present in the data. */
    UnmatchedSlice = 'unmatched-slice',
    /** Renderable, but with an area of only a few pixels. */
    Tiny = 'tiny',
    /** Renderable, but a small part not connected to the rest of the structure (in-plane or cranio-caudally). */
    Isolated = 'isolated',
}

export type StrayContour = {
    kind: StrayContourKind,
    sliceId: string,
    /** Index into image slice ids, -1 if the slice id doesn't match any image slice */
    sliceIndex: number,
    pointCount: number,
    areaMm2: number,
    /** Width and height of the polygon's bounding box in the transversal plane */
    sizeMm: [number, number],
    points: ContourPoint[],
}

export type StrayContourOptions = {
    /** Polygons smaller than this many image pixels are reported as tiny. */
    tinyAreaInPixels: number,
    /** ...and whose width and height are both smaller than this many image pixels. */
    tinyMaxSizeInPixels: number,
    /** Polygons at most this many slices apart can be connected... */
    isolatedGapSlices: number,
    /** ...if their bounding boxes are at most this far apart in the transversal plane. */
    isolatedLinkDistanceMm: number,
    /** Parts not connected to the main part are reported if they span at most this many slices... */
    isolatedMaxSlices: number,
    /** ...and their total area is at most this fraction of the main part's area. */
    isolatedMaxAreaRatio: number,
}

export const DefaultStrayContourOptions: StrayContourOptions = {
    tinyAreaInPixels: 2,
    tinyMaxSizeInPixels: 5,
    isolatedGapSlices: 5,
    isolatedLinkDistanceMm: 5,
    isolatedMaxSlices: 3,
    isolatedMaxAreaRatio: 0.1,
};

/** Polygons may be stored either as arrays of [x, y, z] points or as flat [x0, y0, z0, x1, ...] arrays. */
export function toPoints(polygon: any[]): ContourPoint[] {
    const flat: number[] = [].concat.apply([], polygon as any);
    const points: ContourPoint[] = [];
    for (let i = 0; i + 2 < flat.length; i += 3) {
        points.push([flat[i], flat[i + 1], flat[i + 2]]);
    }
    return points;
}

/** Area of the polygon projected to the transversal plane (shoelace formula). */
export function polygonAreaMm2(points: ContourPoint[]): number {
    let sum = 0;
    for (let i = 0; i < points.length; ++i) {
        const p = points[i];
        const q = points[(i + 1) % points.length];
        sum += p[0] * q[1] - q[0] * p[1];
    }
    return Math.abs(sum) / 2;
}

export function polygonSizeMm(points: ContourPoint[]): [number, number] {
    if (!points.length) { return [0, 0]; }
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    return [Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)];
}

/**
 * Returns [minX, minY, maxX, maxY, z] of a polygon that is smaller than maxSizeMm in both x and y, or null for bigger
 * (or empty) polygons. Stops reading points as soon as the polygon is known to be too big, so it's cheap to call
 * for big polygons too.
 */
export function getSmallPolygonBounds(polygon: any[], maxSizeMm: number): number[] | null {
    const isNested = Array.isArray(polygon[0]);
    const pointCount = isNested ? polygon.length : Math.floor(polygon.length / 3);
    if (pointCount === 0) { return null; }
    const get = (i: number, c: number): number => isNested ? polygon[i][c] : polygon[i * 3 + c];

    let minX = get(0, 0), maxX = minX, minY = get(0, 1), maxY = minY;
    for (let i = 1; i < pointCount; ++i) {
        const x = get(i, 0), y = get(i, 1);
        if (x < minX) { minX = x; } else if (x > maxX) { maxX = x; }
        if (y < minY) { minY = y; } else if (y > maxY) { maxY = y; }
        if (maxX - minX >= maxSizeMm || maxY - minY >= maxSizeMm) { return null; }
    }
    return [minX, minY, maxX, maxY, get(0, 2)];
}

export function findStrayContours(roiContours: RoiContourData, imageSliceIds: string[], pixelAreaMm2: number,
    options: Partial<StrayContourOptions> = {}): StrayContour[] {

    const opts = { ...DefaultStrayContourOptions, ...options };
    const result: StrayContour[] = [];
    const renderable: StrayContour[] = [];

    Object.keys(roiContours).forEach(sliceId => {
        const sliceIndex = imageSliceIds.indexOf(sliceId);
        (roiContours[sliceId].polygons || []).forEach(polygon => {
            const points = toPoints(polygon);
            const contour: StrayContour = {
                kind: StrayContourKind.Degenerate, sliceId, sliceIndex, pointCount: points.length,
                areaMm2: polygonAreaMm2(points), sizeMm: polygonSizeMm(points), points
            };
            if (points.length < 4) {
                result.push(contour);
            } else if (sliceIndex === -1) {
                result.push({ ...contour, kind: StrayContourKind.UnmatchedSlice });
            } else if (contour.areaMm2 < opts.tinyAreaInPixels * pixelAreaMm2
                // Self-intersecting polygons (e.g. figure-8 shapes) can have a near-zero area even when they're big
                && Math.max(...contour.sizeMm) < opts.tinyMaxSizeInPixels * Math.sqrt(pixelAreaMm2)) {
                result.push({ ...contour, kind: StrayContourKind.Tiny });
            } else {
                renderable.push(contour);
            }
        });
    });

    // The part with the largest total area is the structure itself. Parts that are both short and small compared to
    // it are reported; bigger ones are likely intentional (e.g. structures that consist of several similar parts).
    const parts = findConnectedParts(renderable, opts.isolatedGapSlices, opts.isolatedLinkDistanceMm);
    if (parts.length > 1) {
        const areaOf = (part: StrayContour[]) => part.reduce((sum, c) => sum + c.areaMm2, 0);
        const mainArea = Math.max(...parts.map(areaOf));
        parts
            .filter(part => _uniqueSorted(part.map(c => c.sliceIndex)).length <= opts.isolatedMaxSlices
                && areaOf(part) <= opts.isolatedMaxAreaRatio * mainArea)
            .forEach(part => part.forEach(c => result.push({ ...c, kind: StrayContourKind.Isolated })));
    }

    return result.sort((a, b) => a.sliceIndex - b.sliceIndex);
}

/**
 * Groups polygons into connected parts: polygons on the same or nearby slices (at most maxSliceGap slices apart) are
 * connected if their bounding boxes overlap or are at most maxDistanceMm apart in the transversal plane. Comparing
 * bounding boxes errs on the side of connecting things, i.e. of not reporting them.
 */
export function findConnectedParts(contours: StrayContour[], maxSliceGap: number, maxDistanceMm: number): StrayContour[][] {
    const bounds = contours.map(c => polygonBounds(c.points));
    const parent = contours.map((_, i) => i);
    const root = (i: number): number => {
        while (parent[i] !== i) {
            parent[i] = parent[parent[i]];
            i = parent[i];
        }
        return i;
    };

    const order = contours.map((_, i) => i).sort((a, b) => contours[a].sliceIndex - contours[b].sliceIndex);
    for (let a = 0; a < order.length; ++a) {
        for (let b = a + 1; b < order.length; ++b) {
            const i = order[a], j = order[b];
            if (contours[j].sliceIndex - contours[i].sliceIndex > maxSliceGap) { break; }
            if (_areBoundsNear(bounds[i], bounds[j], maxDistanceMm)) { parent[root(i)] = root(j); }
        }
    }

    const parts: { [root: number]: StrayContour[] } = {};
    contours.forEach((c, i) => {
        const r = root(i);
        parts[r] = parts[r] || [];
        parts[r].push(c);
    });
    return Object.values(parts);
}

/** [minX, minY, maxX, maxY] of the points in the transversal plane */
function polygonBounds(points: ContourPoint[]): number[] {
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function _areBoundsNear(a: number[], b: number[], maxDistanceMm: number): boolean {
    const gapX = Math.max(0, a[0] - b[2], b[0] - a[2]);
    const gapY = Math.max(0, a[1] - b[3], b[1] - a[3]);
    return gapX <= maxDistanceMm && gapY <= maxDistanceMm;
}

/**
 * Location of a stray contour, used for stepping through findings in a stable order even when the findings are
 * recalculated after edits: structure (in the order they're checked), then slice, then position on the slice.
 */
export type StrayContourPosition = { structureOrder: number, sliceIndex: number, x: number, y: number };

export function getStrayContourPosition(structureOrder: number, contour: StrayContour): StrayContourPosition {
    const bounds = contour.points.length ? getSmallPolygonBounds(contour.points, Infinity) : null;
    return {
        structureOrder,
        sliceIndex: contour.sliceIndex,
        x: bounds ? (bounds[0] + bounds[2]) / 2 : 0,
        y: bounds ? (bounds[1] + bounds[3]) / 2 : 0,
    };
}

export function compareStrayContourPositions(a: StrayContourPosition, b: StrayContourPosition): number {
    return (a.structureOrder - b.structureOrder) || (a.sliceIndex - b.sliceIndex) || (a.x - b.x) || (a.y - b.y);
}

/** The first item after (direction 1) or the last item before (direction -1) the given position, or null. */
export function findAdjacentByPosition<T extends { position: StrayContourPosition }>(
    items: T[], current: StrayContourPosition, direction: 1 | -1): T | null {

    const sorted = [...items].sort((a, b) => compareStrayContourPositions(a.position, b.position));
    if (direction === 1) {
        return sorted.find(item => compareStrayContourPositions(item.position, current) > 0) || null;
    }
    const before = sorted.filter(item => compareStrayContourPositions(item.position, current) < 0);
    return before.length ? before[before.length - 1] : null;
}

function _uniqueSorted(values: number[]): number[] {
    return Array.from(new Set(values)).sort((a, b) => a - b);
}
